/**
 * Invoice kinds (3.14, FR-ACC-15, FR-ACC-36). Pure rules shared with the
 * mobile app (apps/mobile/src/services/invoiceKinds.ts).
 *
 * `type` stays the e-document channel (e-fatura / e-arşiv / manual);
 * `invoiceKind` is what the document is:
 *   standard    normal sales / purchase invoice
 *   creditNote  return / credit note against an earlier invoice; cannot
 *               exceed what is left uncredited on it
 *   advance     advance payment invoice; later invoices deduct it
 *   progress    progress billing (hakediş): contract amount × cumulative %
 *               minus what was billed before, retention kept back
 * Advance deductions are gross amounts (tax included) that lower the payable
 * amount of a standard or progress invoice.
 */

export const INVOICE_KINDS = ["standard", "creditNote", "advance", "progress"];
export const INVOICE_LANGUAGES = ["tr", "en", "ar"];
export const INVOICE_KIND_ERRORS = [
  "KIND_INVALID", "LANGUAGE_INVALID", "ORIGINAL_REQUIRED", "ORIGINAL_IS_CREDIT", "CURRENCY_MISMATCH",
  "CREDIT_REASON_REQUIRED", "CREDIT_EXCEEDS_ORIGINAL", "DEDUCTION_NOT_ALLOWED", "CONTRACT_REQUIRED",
  "PROGRESS_PCT_INVALID", "RETENTION_PCT_INVALID", "ADVANCE_NOT_FOUND", "ADVANCE_EXCEEDS",
  "DEDUCTIONS_EXCEED_TOTAL", "INVOICE_REFERENCED"
];

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const OPEN = (inv) => inv && inv.status !== "cancelled" && !inv.deletedAt;

export const kindOf = (inv) => (INVOICE_KINDS.includes(inv?.invoiceKind) ? inv.invoiceKind : "standard");

export function lineTotals(items = []) {
  const byRate = {};
  let subtotal = 0;
  let taxTotal = 0;
  for (const it of items || []) {
    const base = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
    const rate = Number(it.taxRate) || 0;
    const tax = (base * rate) / 100;
    subtotal += base;
    taxTotal += tax;
    byRate[rate] = { base: r2((byRate[rate]?.base || 0) + base), tax: r2((byRate[rate]?.tax || 0) + tax) };
  }
  return { subtotal: r2(subtotal), taxTotal: r2(taxTotal), total: r2(subtotal + taxTotal), byRate };
}

/** Amount of `original` not yet covered by open credit notes (the one being edited excluded). */
export function creditableRemaining(original, invoices = [], { excludeId = null } = {}) {
  if (!original) return 0;
  const credited = (invoices || [])
    .filter((i) => OPEN(i) && i.id !== excludeId && kindOf(i) === "creditNote" && i.originalInvoiceId === original.id)
    .reduce((s, i) => s + (Number(i.totalAmount) || 0), 0);
  return r2((Number(original.totalAmount) || 0) - credited);
}

/** Amount of an advance invoice not yet deducted by other invoices. */
export function advanceRemaining(advance, invoices = [], { excludeId = null } = {}) {
  if (!advance) return 0;
  const used = (invoices || [])
    .filter((i) => OPEN(i) && i.id !== excludeId)
    .flatMap((i) => i.advanceDeductions || [])
    .filter((d) => d.invoiceId === advance.id)
    .reduce((s, d) => s + (Number(d.amount) || 0), 0);
  return r2((Number(advance.totalAmount) || 0) - used);
}

/**
 * Progress billing: period amount = contract × cumulative % − contract ×
 * previous %. Tax on the period amount; retention (% of the period amount)
 * is kept back from the payable amount.
 */
export function progressAmounts({ contractAmount, cumulativePct, previousPct = 0, taxRate = 0, retentionPct = 0 } = {}) {
  const contract = Number(contractAmount) || 0;
  const grossToDate = r2((contract * (Number(cumulativePct) || 0)) / 100);
  const previousBilled = r2((contract * (Number(previousPct) || 0)) / 100);
  const periodAmount = r2(grossToDate - previousBilled);
  const tax = r2((periodAmount * (Number(taxRate) || 0)) / 100);
  const retention = r2((periodAmount * (Number(retentionPct) || 0)) / 100);
  return { grossToDate, previousBilled, periodAmount, tax, total: r2(periodAmount + tax), retention };
}

/** Next progress number and the cumulative % already billed on the same contract. */
export function previousProgress(contractRef, invoices = [], { excludeId = null } = {}) {
  const same = (invoices || []).filter((i) => OPEN(i) && i.id !== excludeId && kindOf(i) === "progress"
    && String(i.progress?.contractRef || "").trim() === String(contractRef || "").trim());
  const previousPct = same.reduce((m, i) => Math.max(m, Number(i.progress?.cumulativePct) || 0), 0);
  return { number: same.length + 1, previousPct };
}

export function deductionsTotal(inv) {
  return r2((inv?.advanceDeductions || []).reduce((s, d) => s + (Number(d.amount) || 0), 0));
}

/** What the buyer still pays: total − advances deducted − retention. */
export function payableAmount(inv) {
  return r2((Number(inv?.totalAmount) || 0) - deductionsTotal(inv) - (Number(inv?.progress?.retention) || 0));
}

/** Effect on the receivable: credit notes reduce it. */
export function signedTotal(inv) {
  const total = Number(inv?.totalAmount) || 0;
  return kindOf(inv) === "creditNote" ? -total : total;
}

/**
 * First broken rule for the invoice about to be saved, or null.
 * `invoices` are the owner's other invoices (to find the original and advances).
 */
export function validateInvoiceKind(inv = {}, invoices = []) {
  const kind = inv.invoiceKind || "standard";
  if (!INVOICE_KINDS.includes(kind)) return "KIND_INVALID";
  if (inv.language && !INVOICE_LANGUAGES.includes(inv.language)) return "LANGUAGE_INVALID";
  const total = Number(inv.totalAmount) || 0;
  const byId = new Map((invoices || []).map((i) => [i.id, i]));

  if (kind === "creditNote") {
    const original = byId.get(inv.originalInvoiceId);
    if (!original || !OPEN(original)) return "ORIGINAL_REQUIRED";
    if (kindOf(original) === "creditNote") return "ORIGINAL_IS_CREDIT";
    if ((original.currency || "TRY") !== (inv.currency || "TRY")) return "CURRENCY_MISMATCH";
    if (!String(inv.creditReason || "").trim()) return "CREDIT_REASON_REQUIRED";
    if (total > creditableRemaining(original, invoices, { excludeId: inv.id }) + 0.005) return "CREDIT_EXCEEDS_ORIGINAL";
    if ((inv.advanceDeductions || []).length) return "DEDUCTION_NOT_ALLOWED";
  }

  if (kind === "advance" && (inv.advanceDeductions || []).length) return "DEDUCTION_NOT_ALLOWED";

  if (kind === "progress") {
    const p = inv.progress || {};
    if (!String(p.contractRef || "").trim() || !(Number(p.contractAmount) > 0)) return "CONTRACT_REQUIRED";
    const cum = Number(p.cumulativePct);
    if (!(cum > 0 && cum <= 100) || cum <= (Number(p.previousPct) || 0)) return "PROGRESS_PCT_INVALID";
    const rp = Number(p.retentionPct) || 0;
    if (rp < 0 || rp > 100) return "RETENTION_PCT_INVALID";
  }

  for (const d of inv.advanceDeductions || []) {
    const adv = byId.get(d.invoiceId);
    if (!adv || !OPEN(adv) || kindOf(adv) !== "advance") return "ADVANCE_NOT_FOUND";
    if ((adv.currency || "TRY") !== (inv.currency || "TRY")) return "CURRENCY_MISMATCH";
    if (!(Number(d.amount) > 0) || Number(d.amount) > advanceRemaining(adv, invoices, { excludeId: inv.id }) + 0.005) return "ADVANCE_EXCEEDS";
  }
  if (deductionsTotal(inv) + (Number(inv.progress?.retention) || 0) > total + 0.005) return "DEDUCTIONS_EXCEED_TOTAL";
  return null;
}
